import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  input,
  InputSignal,
  OnDestroy,
  signal,
  Signal,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RouterLink } from '@angular/router';
import { AnimatedSuccessErrorComponent, SpinnerComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { CapacitorService } from '@libs/shared/common';
import { ScannerHandlerControlsModel, ScannerHeaderConfig } from '@libs/shared/models';
import { NavigationService, ScannerHandlerBase, ScannerHandlerResolverService } from '@libs/shared/services';
import { filter, firstValueFrom, from, Observable } from 'rxjs';
import { MlKitScannerService, ScannerUtilService } from '../../services';

@Component({
  selector: 'ui-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    MatSlideToggle,
    RouterLink,
    AnimatedSuccessErrorComponent,
    IconComponent,
    SpinnerComponent,
    MatBadge,
  ],
})
export class ScannerComponent implements AfterViewInit, OnDestroy {
  public handlerKey: InputSignal<string> = input.required();
  public relatedResourceId: InputSignal<string> = input('');

  public controls: Signal<ElementRef<HTMLDivElement>> = viewChild.required<ElementRef<HTMLDivElement>>('controls');
  public sight: Signal<ElementRef<HTMLDivElement>> = viewChild.required<ElementRef<HTMLDivElement>>('sight');
  public animatedSuccessError: Signal<AnimatedSuccessErrorComponent | undefined> = viewChild(AnimatedSuccessErrorComponent);

  public sightEnabled: WritableSignal<boolean> = signal(true);
  public sightHidden: WritableSignal<boolean> = signal(false);
  public orderMode: Signal<boolean> = toSignal(this.scannerUtilService.getIsOrderMode(), { initialValue: false });
  public loading: WritableSignal<boolean> = signal(false);
  public scanProcessing: WritableSignal<boolean> = signal(false);

  public headerConfig: WritableSignal<ScannerHeaderConfig | undefined> = signal(undefined);
  private scannerHandler: ScannerHandlerBase | undefined;

  public constructor(
    private readonly barcodeScanner: MlKitScannerService,
    private readonly capacitorService: CapacitorService,
    private readonly scannerUtilService: ScannerUtilService,
    private readonly scannerHandlerResolver: ScannerHandlerResolverService,
    private readonly destroyRev: DestroyRef,
    private readonly navService: NavigationService,
  ) {
  }

  public ngAfterViewInit(): void {
    this.scannerHandler = this.scannerHandlerResolver.getHandler(this.handlerKey());
    this.headerConfig.set(this.scannerHandler.getHeaderConfig(this.relatedResourceId()));
    void this.scan();
  }

  public ngOnDestroy(): void {
    if (this.capacitorService.isCapacitorApp()) {
      void this.barcodeScanner.StopScan();
    }
  }

  public navigateBack(): Promise<boolean | void> {
    return this.navService.closeOverlayRoute();
  }

  public async toggleTorch(): Promise<void> {
    await this.barcodeScanner.ToggleTorch();
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  public async toggleOrderMode(): Promise<void> {
    const value: boolean = !this.orderMode();

    this.scannerUtilService.setIsOrderMode(value);
  }

  public async MultiScan(): Promise<void> {
    if (!this.scannerHandler) {
      return;
    }

    this.sightEnabled.set(false);
    const value: string = await this.barcodeScanner.MultiScan(this.controls().nativeElement);

    await firstValueFrom(this.scannerHandler.handleMultiScan(value, this.relatedResourceId()));
  }

  public async ToggleSight(): Promise<void> {
    this.sightEnabled.set(!this.sightEnabled());
    await this.barcodeScanner.StopScan();
    await this.scan();
  }

  public resizeSight(event: TouchEvent): void {
    const sight: HTMLDivElement = this.sight().nativeElement;
    const threshold: number = 50;

    let width: number = event.touches[0].clientX - sight.getBoundingClientRect().left;

    if (width < threshold) {
      width = threshold;
    }

    let height: number = event.touches[0].clientY - sight.getBoundingClientRect().top;

    if (height < threshold) {
      height = threshold;
    }

    sight.style.width = `${width}px`;
    sight.style.height = `${height}px`;

    this.barcodeScanner.CalculateSightCornerDetectionPoints(sight);
  }

  public async scan(): Promise<void> {
    if (!this.scannerHandler) {
      return;
    }

    const handler: ScannerHandlerBase = this.scannerHandler;

    const values: Observable<string> = await this.barcodeScanner.Scan(this.sightEnabled() ? this.sight().nativeElement : undefined, this.orderMode, this.scanProcessing);

    const controlParams: ScannerHandlerControlsModel = {
      showLoader: () => void this.loading.set(true),
      hideLoader: () => void this.loading.set(false),
      showSuccess: () => from(this.showSuccess()),
      showError: () => from(this.showError()),
    };

    // throttleTime or something similar won't work here because we cant know how long the process below will take
    values.pipe(takeUntilDestroyed(this.destroyRev), filter(() => !this.scanProcessing())).subscribe((value) => {
      if (!this.orderMode()) {
        void firstValueFrom(handler.handleOrderModeDisabledScan(value, controlParams, this.relatedResourceId()));

        return;
      }

      this.scanProcessing.set(true);
      this.sightHidden.set(true);
      handler.handleOrderModeEnabledScan(value, controlParams, this.relatedResourceId()).subscribe(() => {
        this.sightHidden.set(false);

        // we delay this here because otherwise the scanner would scan the current barcode again
        // this improves user experience.
        setTimeout(() => {
          this.scanProcessing.set(false);
        }, 1000);
      });
    });
  }

  private async showSuccess(): Promise<void> {
    const animatedSuccessError: AnimatedSuccessErrorComponent | undefined = this.animatedSuccessError();

    if (!animatedSuccessError) {
      return;
    }

    await animatedSuccessError.showSuccess();
  }

  private async showError(): Promise<void> {
    const animatedSuccessError: AnimatedSuccessErrorComponent | undefined = this.animatedSuccessError();

    if (!animatedSuccessError) {
      return;
    }

    await animatedSuccessError.showError();
  }
}
